import React, { useState, useEffect } from 'react';
import { ExternalLink, ChevronRight, Lock } from 'lucide-react';
import { apiService } from '../services/apiService';
import config from '../config.json';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import VpnModal, { useVpnStatus } from '../components/Vpn/VpnModal';

interface AppData {
  id: string;
  name: string;
  description: string;
  icon: string;
  url: string;
  color: string;
  iconBg: string;
  api_id?: string;
}

const Apps: React.FC = () => {
  const [availableApps, setAvailableApps] = useState<AppData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The VPN isn't one of the launchable apps built from /crm/services -- it has
  // its own status endpoint and opens in place instead of redirecting out.
  const { peer: vpnPeer, available: vpnAvailable, installerAvailable: vpnInstallerAvailable } = useVpnStatus();
  const [vpnOpen, setVpnOpen] = useState(false);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await apiService.getServices();
        const services = response.result || []; // Extract the result array from the API response
        const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
        const token = userDetails.access_token;
        
        const apps: AppData[] = [];
        
        services.forEach((service: any) => {
          if (service.item_type === 'itp_voice') {
            const linkPbx = config.prod ? config.pbx.prod : config.pbx.dev;
            const url = `${linkPbx}/login?token=${token}&&v2=${service.api_id}`;
            
            apps.push({
              id: 'voice360',
              name: 'VOICE360',
              description: 'Business communications platform',
              icon: '📞',
              url,
              color: 'bg-blue-100 border-blue-200',
              iconBg: 'bg-blue-500',
              api_id: service.api_id,
            });
          } else if (service.item_type === 'PayLink360') {
            const payLink = config.prod ? config.payLink.prod : config.payLink.dev;
            const url = `${payLink}/login?token=${token}&&v2=${service.api_id}`;
            
            apps.push({
              id: 'paylink360',
              name: 'PayLink360',
              description: 'Payment processing and billing',
              icon: '💳',
              url,
              color: 'bg-orange-100 border-orange-200',
              iconBg: 'bg-orange-500',
              api_id: service.api_id,
            });
          } else if (service.item_type === 'office365') {
            apps.push({
              id: 'office365',
              name: 'Microsoft 365',
              description: 'Office productivity suite',
              icon: '📊',
              url: 'https://office.com',
              color: 'bg-indigo-100 border-indigo-200',
              iconBg: 'bg-indigo-500',
              api_id: service.api_id,
            });
          } else if (service.item_type === 'google_workspace' || service.item_type === 'google_apps') {
            apps.push({
              id: 'google_workspace',
              name: 'Google Workspace',
              description: 'Collaboration and productivity tools',
              icon: '🌟',
              url: 'https://workspace.google.com',
              color: 'bg-green-100 border-green-200',
              iconBg: 'bg-green-500',
              api_id: service.api_id,
            });
          } else if (service.item_type === 'it_managed_services' && userDetails.auth?.admin_group) {
            // IT Assets Manager - hidden page accessible only to admins with managed IT services
            apps.push({
              id: 'it_assets',
              name: 'IT Asset Manager',
              description: 'Manage IT assets, vendors, and credentials',
              icon: '🖥️',
              url: '/it-assets', // Internal route to hidden page
              color: 'bg-purple-100 border-purple-200',
              iconBg: 'bg-purple-500',
              api_id: service.api_id,
            });
          } else if (service.item_type === 'itp_sdaas') {
            // Software Development as a Service - internal project requests portal
            apps.push({
              id: 'software_development',
              name: 'Software Development',
              description: 'Submit change requests and track your project',
              icon: '💻',
              url: '/software-development',
              color: 'bg-violet-100 border-violet-200',
              iconBg: 'bg-violet-500',
              api_id: service.api_id,
            });
          } else if (service.item_type === 'itp_voice_legacy') {
            // Legacy VoiceLinx Phone System - internal management page
            apps.push({
              id: 'voice_legacy',
              name: 'VoiceLinx Legacy',
              description: 'Manage legacy VoiceLinx phone system settings',
              icon: '☎️',
              url: '/legacy-voice', // Internal route to legacy voice management page
              color: 'bg-slate-100 border-slate-200',
              iconBg: 'bg-slate-500',
              api_id: service.api_id,
            });
          }
        });
        
        // Deduplicate by id — multiple service instances (e.g. 4x office365) show once
        const seen = new Set<string>();
        const uniqueApps = apps.filter(app => {
          if (seen.has(app.id)) return false;
          seen.add(app.id);
          return true;
        });
        setAvailableApps(uniqueApps);
      } catch (err) {
        console.error('Failed to fetch services:', err);
        setError('Failed to load available applications');
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const openApp = (url: string) => {
    if (url === '#') {
      // Handle placeholder URLs - could show a "Coming Soon" message
      alert('This application will be available soon!');
      return;
    }
    
    // Handle internal routes (like IT Assets) vs external URLs
    if (url.startsWith('/')) {
      // Internal route - use React Router navigation
      window.location.pathname = url;
    } else {
      // External URL - redirect with token
      window.location.href = url;
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="mt-2 text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Apps</h1>
        <p className="mt-2 text-gray-600">Access your ITP360 applications</p>
      </div>

      {availableApps.length === 0 && !vpnAvailable ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Applications Available</h3>
          <p className="text-gray-600">You don't have access to any applications yet. Contact support if you need assistance.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
          {availableApps.map((app) => (
            <div
              key={app.id}
              className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 cursor-pointer group transition-colors"
              onClick={() => openApp(app.url)}
            >
              <div className={`w-10 h-10 ${app.iconBg} rounded-lg flex items-center justify-center text-lg flex-shrink-0`}>
                {app.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">{app.name}</p>
                <p className="text-xs text-gray-500 truncate">{app.description}</p>
              </div>
              <ExternalLink className="h-4 w-4 text-gray-300 group-hover:text-gray-500 transition-colors flex-shrink-0" />
            </div>
          ))}

          {vpnAvailable && (
            <div
              className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 cursor-pointer group transition-colors"
              onClick={() => setVpnOpen(true)}
            >
              <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <Lock className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">ITP VPN Service</p>
                <p className="text-xs text-gray-500 truncate">Secure remote access to your office network</p>
              </div>
              {/* Chevron, not the external-link icon the other rows use: this one
                  opens in place rather than sending you off to another app. */}
              <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-gray-500 transition-colors flex-shrink-0" />
            </div>
          )}
        </div>
      )}

      <VpnModal
        peer={vpnPeer}
        installerAvailable={vpnInstallerAvailable}
        isOpen={vpnOpen}
        onClose={() => setVpnOpen(false)}
      />
    </div>
  );
};

export default Apps;
